/**
 * Estado de autenticación de la app. Mantiene el usuario actual (hidratado
 * desde localStorage), expone login/registro/logout y conecta el cliente HTTP:
 * cuando el refresh transparente falla, `setOnUnauthorized` fuerza el cierre de
 * sesión aquí. El resto de la app lee el usuario/rol vía `useAuth`.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { setOnUnauthorized } from "../api/client.js";
import { authApi } from "../api/endpoints.js";
import { clearSession, loadTokens, loadUser, saveSession } from "../api/tokenStore.js";
import type { Rol, Usuario } from "../api/types.js";

interface AuthValue {
  usuario: Usuario | null;
  rol: Rol | null;
  cargando: boolean;
  login: (email: string, password: string) => Promise<Usuario>;
  registrar: (input: {
    email: string;
    password: string;
    nombre: string;
    rol: "padre" | "terapeuta";
  }) => Promise<Usuario>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [usuario, setUsuario] = useState<Usuario | null>(null);
  const [cargando, setCargando] = useState(true);

  // Hidratación inicial: si hay tokens + usuario en storage, restauramos.
  useEffect(() => {
    if (loadTokens() && loadUser()) setUsuario(loadUser());
    setCargando(false);
  }, []);

  // El cliente HTTP avisa cuando el refresh también expiró → cerramos sesión.
  useEffect(() => {
    setOnUnauthorized(() => setUsuario(null));
    return () => setOnUnauthorized(null);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const sesion = await authApi.login({ email, password });
    saveSession(sesion);
    setUsuario(sesion.usuario);
    return sesion.usuario;
  }, []);

  const registrar = useCallback<AuthValue["registrar"]>(async (input) => {
    const sesion = await authApi.registrar(input);
    saveSession(sesion);
    setUsuario(sesion.usuario);
    return sesion.usuario;
  }, []);

  const logout = useCallback(async () => {
    const tokens = loadTokens();
    if (tokens) {
      try {
        await authApi.logout(tokens.refreshToken);
      } catch {
        /* logout best-effort: aunque falle, limpiamos localmente */
      }
    }
    clearSession();
    setUsuario(null);
  }, []);

  const value = useMemo<AuthValue>(
    () => ({ usuario, rol: usuario?.rol ?? null, cargando, login, registrar, logout }),
    [usuario, cargando, login, registrar, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth fuera de AuthProvider");
  return ctx;
}
